import { bindable, customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './collection-tree-modal.html';
import { getCollectionTree, moveCollection } from '../services/api.service';
import type { CollectionTreeNode as CollectionTreeNodeDto } from '../services/api.types';
import type { CollectionTreeItem } from './collection-tree-node';

/** Whole-collection-tree view (folder structure only, no media items) with drag-and-drop
 *  reparenting — lets the user restructure their collections, e.g. create a new top-level
 *  collection and drag an existing one onto it to make it a subcollection, or drag a
 *  subcollection onto the "root" drop target to pull it back out to the top level. */
@customElement({ name: 'collection-tree-modal', template })
export class CollectionTreeModal {
  private readonly i18n: I18N = resolve(I18N);

  @bindable public closed!: () => void;
  // Fired after a successful move so the caller (collections-view) can refresh its own listing —
  // the moved collection may have been the one currently being viewed, or a sibling of it.
  @bindable public moved?: () => void;

  public roots: CollectionTreeItem[] = [];
  public isLoading = false;
  public error = '';

  public draggedId: string | null = null;
  public dragOverId: string | null = null;
  public rootDragOver = false;
  // The dragged node's own id plus every one of its descendants' ids — dropping onto any of
  // these would either be a no-op (itself) or detach the subtree into a cycle (a descendant).
  private _draggedDescendantIds = new Set<string>();
  private _nodesById = new Map<string, CollectionTreeItem>();

  public async attached(): Promise<void> {
    await this._load();
  }

  private async _load(): Promise<void> {
    this.isLoading = true;
    this.error = '';
    try {
      const nodes = await getCollectionTree();
      this.roots = this._buildTree(nodes);
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.isLoading = false;
    }
  }

  private _buildTree(nodes: CollectionTreeNodeDto[]): CollectionTreeItem[] {
    this._nodesById = new Map();
    const items = new Map<string, CollectionTreeItem>();
    for (const n of nodes) {
      items.set(n.id, { id: n.id, title: n.title, parentId: n.parentId, children: [], expanded: true });
    }

    const roots: CollectionTreeItem[] = [];
    for (const item of items.values()) {
      this._nodesById.set(item.id, item);
      const parent = item.parentId ? items.get(item.parentId) : undefined;
      if (parent) parent.children.push(item);
      else roots.push(item);
    }

    const byTitle = (a: CollectionTreeItem, b: CollectionTreeItem) =>
      a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
    const sortRecursively = (list: CollectionTreeItem[]): void => {
      list.sort(byTitle);
      for (const item of list) sortRecursively(item.children);
    };
    sortRecursively(roots);
    return roots;
  }

  // Bound to child `<collection-tree-node>` instances — arrow-function class properties so
  // `this` stays bound to the modal when the child invokes them directly from its own template
  // (a plain method reference passed through a bindable loses its `this` otherwise).
  public toggle = (node: CollectionTreeItem): void => {
    node.expanded = !node.expanded;
    // Aurelia doesn't deep-observe nested object graphs — reassign `roots` to a new array
    // reference so the `repeat.for` chain re-evaluates the (mutated) `expanded` flag.
    this.roots = [...this.roots];
  };

  private _descendantIds(node: CollectionTreeItem): Set<string> {
    const result = new Set<string>([node.id]);
    const stack = [...node.children];
    while (stack.length > 0) {
      const current = stack.pop()!;
      result.add(current.id);
      stack.push(...current.children);
    }
    return result;
  }

  private _canDropOn(targetId: string | null): boolean {
    if (!this.draggedId) return false;
    if (targetId === null) return true;
    return !this._draggedDescendantIds.has(targetId);
  }

  public onDragStart = (node: CollectionTreeItem, event: DragEvent): void => {
    this.draggedId = node.id;
    this._draggedDescendantIds = this._descendantIds(node);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', node.id);
    }
  };

  public onDragOver = (node: CollectionTreeItem, event: DragEvent): void => {
    if (!this._canDropOn(node.id)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    this.dragOverId = node.id;
  };

  public onDragLeave = (): void => {
    this.dragOverId = null;
  };

  public onRootDragOver = (event: DragEvent): void => {
    if (!this._canDropOn(null)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    this.rootDragOver = true;
  };

  public onRootDragLeave = (): void => {
    this.rootDragOver = false;
  };

  public onDrop = (node: CollectionTreeItem, event: DragEvent): void => {
    event.preventDefault();
    void this._move(node.id);
  };

  public onRootDrop = (event: DragEvent): void => {
    event.preventDefault();
    void this._move(null);
  };

  public onDragEnd = (): void => {
    this.draggedId = null;
    this.dragOverId = null;
    this.rootDragOver = false;
    this._draggedDescendantIds = new Set();
  };

  private async _move(newParentId: string | null): Promise<void> {
    const sourceId = this.draggedId;
    // Validate the drop *before* clearing the drag state below — `_canDropOn`
    // reads `this.draggedId`/`this._draggedDescendantIds`, both of which are reset
    // right after, so checking afterwards always failed and silently no-op'd every drop.
    const canDrop = this._canDropOn(newParentId);
    this.draggedId = null;
    this.dragOverId = null;
    this.rootDragOver = false;
    if (!sourceId || !canDrop) return;

    const source = this._nodesById.get(sourceId);
    if (!source || source.parentId === newParentId) return;

    this.error = '';
    try {
      await moveCollection(sourceId, newParentId);
      await this._load();
      this.moved?.();
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
    }
  }

  public close(): void {
    this.closed();
  }
}
